import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Chip, FormField, Modal, Select } from '@/components/ui';
import { AddRoleCombobox } from './AddRoleCombobox';
import { CreateAppRoleModal } from '../appRoles/CreateAppRoleModal';
import { RoleMapStaleCatalogCard } from './RoleMapStaleCatalogCard';
import { isStaleCatalogError } from './useRoleMapWrites';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1837: "+ Thêm liên kết" — the header's primary action.
 *
 * Deliberately allows SEVERAL app roles in one submit: that is how a multi-role
 * mapping (`teacher_lead → lms: teacher + admin_class`) comes into existence,
 * since it is N `RoleMap` rows sharing one `(role_key, application)` pair. The
 * app-role picker is the EAA-1817 `AddRoleCombobox` unchanged — it lists only
 * what `GET /admin/app-roles?application=` reports and accepts no free text, so
 * a link can never be created against a role the app does not declare.
 *
 * `role_key` options (`vm.standardRoles`) come from the server's governed
 * role-key vocabulary (`GET /role-keys`, EAA-1817) rather than the acting
 * org's own Levels — see `standardRolesFromRoleKeys` in `types.ts` for the
 * fallback when that vocabulary is empty; when even the fallback has nothing,
 * the modal says so instead of offering a free-text field that would create a
 * mapping nothing resolves to.
 */
export function RoleMapAddModal({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const [roleKey, setRoleKey] = useState('');
    const [appRoles, setAppRoles] = useState<string[]>([]);

    const appCode = vm.addApp;

    useEffect(() => {
        if (!vm.addOpen) return;
        setRoleKey('');
        setAppRoles([]);
        vm.setAddApp(null);
        vm.createMutation.reset();
        // Only reset when the modal opens; `vm.setAddApp`/`vm.createMutation.reset`
        // are stable across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vm.addOpen]);

    // Changing the application invalidates any chip picked from the previous
    // app's catalog — those keys mean nothing to the new app.
    useEffect(() => {
        setAppRoles([]);
    }, [appCode]);

    const scopeLabel =
        vm.scope === 'default'
            ? t('console.roleMap.toolbar.scopeDefaultLabel')
            : (vm.actingOrgName ?? t('console.roleMap.toolbar.scopeGenericFallback'));
    const enabledApps = useMemo(() => vm.apps.filter((app) => app.enabled), [vm.apps]);
    const selectedApp = useMemo(
        () => vm.apps.find((app) => app.code === appCode) ?? null,
        [vm.apps, appCode]
    );

    /**
     * EAA-1841: the escape hatch out of this modal's original dead-end — the
     * combobox accepts no free text, so a role the app has not declared could
     * only be created by leaving the console entirely.
     *
     * Offered ONLY in the org scope, and that restriction is load-bearing
     * rather than cautious: `POST /admin/app-roles` always upserts the row
     * ORG-SCOPED, and this modal's picker only asks for an org's own catalog
     * rows when it is writing org-scoped links (see `addAppRolesOrg` in
     * `useRoleMapTable`). In the default scope the new role would be created
     * and then be invisible here — the "button that sometimes silently does
     * nothing" the backend's opt-in flag exists to avoid. `/console/apps` still
     * offers the action unconditionally for creating a role outside any
     * linking flow.
     */
    const canCreateRole = Boolean(
        selectedApp?.supports_role_management && vm.scope === 'org' && vm.addAppRolesOrg
    );
    const canSubmit = Boolean(roleKey) && Boolean(appCode) && appRoles.length > 0 && !vm.addBusy;

    const { error: createError, reset: resetCreateError } = vm.createMutation;
    const staleError = isStaleCatalogError(createError) ? createError : null;

    // EAA-1817: "Thẻ đã được bỏ khỏi danh sách" (frame 51) — the chip that
    // tripped the catalog check is removed from the pending selection the
    // moment the error lands, so "Thử lại" can never resubmit the exact same
    // broken request.
    useEffect(() => {
        if (!staleError) return;
        setAppRoles((prev) => prev.filter((key) => key !== staleError.appRole));
    }, [staleError]);

    const staleAppName =
        vm.apps.find((app) => app.code === staleError?.application)?.name ??
        staleError?.application ??
        '';

    const submit = () => {
        if (!appCode || appRoles.length === 0) return;
        void vm.createLinks({ role_key: roleKey, application: appCode, appRoles });
    };

    // EAA-1841: resolved from the registry rather than reusing `selectedApp` so
    // the create modal keeps its target even if the app select is changed while
    // it is open.
    const roleCreateTarget = vm.apps.find((app) => app.code === vm.roleCreateApp) ?? null;

    return (
        <>
            <Modal
                open={vm.addOpen}
                onCancel={vm.closeAdd}
                title={t('console.roleMap.addModal.title')}
                footer={
                    <>
                        <Button onClick={vm.closeAdd} disabled={vm.addBusy}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="primary" disabled={!canSubmit} onClick={submit}>
                            {vm.addBusy
                                ? t('console.roleMap.addModal.saving')
                                : // EAA-1845: `roleCount` (not `count`) on purpose — i18next
                                  // reserves the `count` option key for its own pluralization
                                  // rules and requires it to be a number, but this needs the
                                  // original quirky "" fallback (see the historical `.trim()`
                                  // note below) to keep matching the pre-i18n markup exactly.
                                  t('console.roleMap.addModal.submitButton', {
                                      roleCount: appRoles.length || '',
                                  }).trim()}
                        </Button>
                    </>
                }
            >
                <div className="portal-admin-form" style={{ gridTemplateColumns: '1fr' }}>
                    <p className="portal-admin-hint">
                        <Trans
                            i18nKey="console.roleMap.addModal.scopeHint"
                            values={{ scopeLabel }}
                            components={[<strong key="scope" />]}
                        />
                    </p>

                    <FormField
                        label={t('console.roleMap.addModal.roleKeyLabel')}
                        hint={
                            // EAA-1817: copy updated for the new source — `standardRoles` now
                            // comes from the governed role-key vocabulary (`GET /role-keys`),
                            // falling back to this org's existing rows, not from Levels — so
                            // "chưa khai báo vai trò chuẩn" no longer points at "Cơ cấu tổ
                            // chức" specifically; nothing governed exists anywhere yet.
                            vm.standardRoles.length === 0
                                ? t('console.roleMap.addModal.noStandardRolesHint')
                                : undefined
                        }
                    >
                        {/* EAA-1817: kit `Select`'s `onChange` passes the selected value
                        directly (not a DOM event), so `event.target.value` becomes just
                        `value`. The empty-string placeholder is kept as a selectable
                        option (not the `placeholder` prop) to match the previous
                        native-select behavior exactly. */}
                        <Select
                            value={roleKey}
                            onChange={(value) => setRoleKey(value as string)}
                            style={{ width: '100%' }}
                            options={[
                                {
                                    value: '',
                                    label: t('console.roleMap.addModal.roleKeyPlaceholderOption'),
                                },
                                ...vm.standardRoles.map((role) => ({
                                    value: role.role_key,
                                    label: `${role.role_key} — ${role.name}`,
                                })),
                            ]}
                        />
                    </FormField>

                    <FormField label={t('console.roleMap.addModal.appLabel')}>
                        <Select
                            value={appCode ?? ''}
                            onChange={(value) => vm.setAddApp((value as string) || null)}
                            style={{ width: '100%' }}
                            options={[
                                {
                                    value: '',
                                    label: t('console.roleMap.addModal.appPlaceholderOption'),
                                },
                                ...enabledApps.map((app) => ({
                                    value: app.code,
                                    label: `${app.code} — ${app.name}`,
                                })),
                            ]}
                        />
                    </FormField>

                    <div className="rm-add-roles">
                        <span className="rm-add-roles__label">
                            {t('console.roleMap.addModal.appRolesLabel')}
                        </span>
                        <div className="rm-add-roles__chips">
                            {appRoles.map((key) => (
                                <Chip
                                    key={key}
                                    tone="mapped"
                                    onRemove={() =>
                                        setAppRoles((prev) => prev.filter((k) => k !== key))
                                    }
                                    removeLabel={t('console.roleMap.addModal.removeChipLabel', {
                                        key,
                                    })}
                                >
                                    {key}
                                </Chip>
                            ))}
                            <AddRoleCombobox
                                appName={appCode ?? t('console.roleMap.addModal.genericAppFallback')}
                                options={vm.addAppRoles}
                                loading={vm.addAppRolesLoading}
                                linkedAppRoles={appRoles}
                                onSelect={(key) => setAppRoles((prev) => [...prev, key])}
                                disabled={!appCode}
                            />
                        </div>
                        {canCreateRole && (
                            <p className="portal-admin-hint">
                                {t('console.roleMap.addModal.createRoleQuestion')}{' '}
                                <Button onClick={() => vm.openRoleCreate(appCode as string)}>
                                    {t('console.roleMap.addModal.createRoleButton', {
                                        appName: selectedApp?.name,
                                    })}
                                </Button>
                            </p>
                        )}
                    </div>

                    {staleError && (
                        <RoleMapStaleCatalogCard
                            appName={staleAppName}
                            appRole={staleError.appRole}
                            syncBusy={vm.syncMutation.isPending}
                            onSync={() => {
                                vm.syncMutation.mutate();
                                resetCreateError();
                            }}
                            onRetry={appRoles.length > 0 ? submit : undefined}
                        />
                    )}
                </div>
            </Modal>

            {/* EAA-1841: the just-created role is added to the pending selection
            straight away — the operator asked for it because they wanted to
            link it, so making them re-find it in the combobox would undo the
            point of closing the dead-end. */}
            <CreateAppRoleModal
                app={roleCreateTarget}
                onClose={vm.closeRoleCreate}
                onCreated={(role) =>
                    setAppRoles((prev) => (prev.includes(role.key) ? prev : [...prev, role.key]))
                }
            />
        </>
    );
}
